import { DOCUMENT, ErrorHandler, Injectable, inject } from '@angular/core';

export const CHUNK_RELOAD_STORAGE_KEY = 'chunk-reload-last-attempt';
export const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

// Este proyecto usa esbuild: un chunk lazy que ya no existe falla con el error nativo de `import()`,
// cuyo texto depende del navegador (no hay `ChunkLoadError` de webpack).
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

const MAX_DEPTH = 5;

/**
 * ¿El error viene de un chunk lazy que no se pudo cargar (típico tras un deploy con la pestaña abierta)?
 * El router puede envolver el error original y zone.js lo deja en `rejection`: se mira el mensaje,
 * `cause` y `rejection`, con tope de profundidad por si hay ciclos.
 */
export function isChunkLoadError(error: unknown, depth = 0): boolean {
  if (depth > MAX_DEPTH || error === null || error === undefined) return false;
  if (typeof error === 'string') return CHUNK_ERROR_PATTERN.test(error);
  if (typeof error !== 'object') return false;

  const { message, cause, rejection } = error as { message?: unknown; cause?: unknown; rejection?: unknown };
  return (
    (typeof message === 'string' && CHUNK_ERROR_PATTERN.test(message)) ||
    isChunkLoadError(cause, depth + 1) ||
    isChunkLoadError(rejection, depth + 1)
  );
}

/**
 * Si un deploy nuevo borró los chunks que la pestaña abierta todavía referencia, recarga la página una vez
 * para traer el index nuevo. Un cooldown de 60 s (en sessionStorage) evita el bucle de recargas si el chunk
 * sigue sin estar. Cualquier otro error se loguea igual que el handler por defecto.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly view = inject(DOCUMENT).defaultView;

  handleError(error: unknown): void {
    if (isChunkLoadError(error) && this.reloadOnce()) {
      return;
    }
    console.error(error);
  }

  /** `true` si disparó la recarga. */
  private reloadOnce(): boolean {
    const view = this.view;
    if (!view) return false;

    const now = Date.now();
    try {
      const last = Number(view.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY));
      if (last && now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
    } catch {
      // Sin storage legible se trata como "sin intento previo".
    }

    try {
      view.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    } catch {
      // Si no se puede registrar el intento no hay cooldown posible: mejor no recargar que arriesgar un bucle.
      return false;
    }

    view.location.reload();
    return true;
  }
}
